'use client';

import { Drawer, Heading, TextPassage } from '@petsmart-ui/sparky';
import cn from 'classnames';
import { type MouseEvent, useEffect, useId, useState } from 'react';

import { useAutoshipContentQuery } from '@libs/common-next';
import { parseRichText } from '@libs/contentful';

import { StackedDrawerBackButton } from '../stacked-drawer-boundary';
import { AutoshipInfoDrawerHeader } from './AutoshipInfoDrawerHeader';
import { isSkuMapRestricted } from './lib';

import './AutoshipInfoDrawer.scss';

type AutoshipInfoDrawerProps = {
    discountDisplay?: string;
    isFirstAutoshipEligible: boolean;
    isDrawerOpen: boolean;
    setIsDrawerOpen: (isDrawerOpen: boolean) => void;
    dataLayerPushEvent?: () => void;
    showBackButton?: boolean;
    onCloseAll?: () => void;
};

export const AutoshipInfoDrawer = (props: AutoshipInfoDrawerProps) => {
    const { isDrawerOpen, setIsDrawerOpen, showBackButton = false, onCloseAll } = props;
    const { autoshipContent, autoshipMapRestrictedContent } = useAutoshipContentQuery();
    const headingId = useId();

    // Don't render drawer on server or first client render to prevent Sparky animation issues
    // This avoids scroll lock, focus trap, and aria-modal activation on initial page load
    const [hasBeenRendered, setHasBeenRendered] = useState(false);

    useEffect(() => {
        setHasBeenRendered(true);
    }, []);

    useEffect(() => {
        if (!isDrawerOpen) {
            return;
        }

        props.dataLayerPushEvent?.();
    }, [isDrawerOpen]);

    const selectedContent = isSkuMapRestricted(props.discountDisplay)
        ? (autoshipMapRestrictedContent ?? autoshipContent)
        : autoshipContent;

    if (!selectedContent || !hasBeenRendered) return null;

    const { header, footer, body } = selectedContent;
    const parsedFooter = footer ? parseRichText(footer) : null;
    const parsedBody = body ? parseRichText(body) : null;

    const handleBack = (event: MouseEvent) => {
        event.stopPropagation();
        event.preventDefault();
        setIsDrawerOpen(false);
    };

    const handleClose = () => {
        setIsDrawerOpen(false);
        onCloseAll?.();
    };

    return (
        <Drawer
            align="right"
            isActive={isDrawerOpen}
            onClose={handleClose}
            data-testid="autoship-info-drawer"
            className={cn('autoship-info-drawer', { 'autoship-info-drawer--stacked': showBackButton })}
            ariaLabelledBy={headingId}
            dismissible
        >
            <Drawer.Header>
                {showBackButton && (
                    <StackedDrawerBackButton
                        className="autoship-info-drawer__back"
                        testId="autoship-info-drawer-back-btn"
                        onClick={handleBack}
                    />
                )}
                <Heading id={headingId} tagName="h2" size="headline" data-testid="drawer-title">
                    {header}
                </Heading>
            </Drawer.Header>

            <Drawer.Body data-testid="drawer-body">
                <AutoshipInfoDrawerHeader {...props} />
                <div className="autoship-info-drawer__static-content">{parsedBody}</div>
            </Drawer.Body>

            {parsedFooter && (
                <Drawer.Footer data-testid="drawer-footer">
                    <TextPassage>{parsedFooter}</TextPassage>
                </Drawer.Footer>
            )}
        </Drawer>
    );
};
